<template>
  <div class="form-input-element" :data-type="type">
    <form-antd-select
      v-bind="antDesignProps || {}"
      :allowClear="allowEmpty || allowClear"
      :labelInValue="labelInValue !== undefined ? labelInValue : true"
      :label="label"
      :searchable="searchable"
      :multiple="multiple"
      :mode="mode"
      :trackBy="_trackBy"
      :options="_options"
      :modelValue="_value"
      @update:value="returnValue"
      @keydown.tab="handleTab"
      :placeholder="placeholder || ''"
      data-lpignore="true"
      ref="smartSelectAnt"
    >
      <template v-slot:noResult>
        <slot name="noResult">No matches found.</slot>
      </template>
      <template v-slot:placeholder>
        <slot name="placeholder">
          <div class="flex ml-1 text-gray-400">
            {{ placeholder || '' }}
          </div>
        </slot>
      </template>
      <template v-slot:singleLabel="{ option }">
        <slot name="singleLabel" :option="option">
          {{ option?.label || '' }}
        </slot>
      </template>
      <template v-slot:option="{ option }">
        <slot name="option" :key="option?.key || option?.value" :option="option">
          {{ option?.label || '' }}
        </slot>
      </template>
      <template v-slot:tag="props">
        <slot name="tag" v-bind="props" :remove="props.onClose">
          <a-tag :closable="props?.closable" :title="props?.label" @close="props?.onClose">
            {{ props?.label || '' }}
          </a-tag>
        </slot>
      </template>
    </form-antd-select>
  </div>
</template>

<script>
import FormAntdSelect from './AntDesign/FormAntdSelect.vue'

export default {
  name: 'FormSmartSelect',
  inheritAttrs: false,
  components: {
    FormAntdSelect
  },
  props: {
    /**
     * Label from option Object, that will be visible in the dropdown.
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Current value.
     * @model
     */
    modelValue: {
      type: [String, Number, Array, Object],
      default: null
    },
    /**
     * Available options.
     */
    smartOptions: {
      type: [Array, Object],
      required: true
    },
    /**
     * Track by field.
     */
    trackBy: {
      type: String,
      default: 'value'
    },
    /**
     * Placeholder text.
     */
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * Allow clearing selection.
     */
    allowEmpty: {
      type: Boolean,
      default: false
    },
    /**
     * Allow clearing selection (alias).
     */
    allowClear: {
      type: Boolean,
      default: false
    },
    /**
     * Return label in value.
     */
    labelInValue: {
      type: Boolean,
      default: true
    },
    /**
     * Is searchable.
     */
    searchable: {
      type: Boolean,
      default: true
    },
    /**
     * Multiple selection.
     */
    multiple: {
      type: Boolean,
      default: false
    },
    /**
     * Select mode.
     */
    mode: {
      type: String,
      default: ''
    },
    /**
     * Select on tab.
     */
    selectOnTab: {
      type: Boolean,
      default: false
    },
    /**
     * Ant Design props.
     */
    antDesignProps: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['object', 'input', 'update:modelValue'],
  data() {
    return {
      initialValue: null
    }
  },
  computed: {
    _value() {
      let val
      if (typeof this.modelValue != 'object') {
        val = this._options.find((item) => item[this._trackBy] == this.modelValue)
      } else {
        if (Array.isArray(this.modelValue) && typeof this.modelValue[0] != 'object') {
          // Must be string values so convert to objects.
          val = this.modelValue.map((item) => {
            return this._options.find((o) => o[this._trackBy] == item) ?? { value: item, label: item }
          })
        } else {
          val = this.modelValue
        }
      }

      return val
    },
    _options() {
      let options = this.smartOptions

      if (!Array.isArray(options) && typeof options == 'object') {
        let _options = []

        Object.keys(options).forEach((item) => {
          _options.push({ value: item, label: options[item] })
        })

        return _options
      } else if (typeof options[0] == 'string' || typeof options[0] == 'number') {
        return options.map((item) => {
          return { value: item, label: item }
        })
      }
      return options
    },
    _trackBy() {
      return this.trackBy
    }
  },
  created() {
    this.initialValue = this.modelValue ?? ''
  },
  mounted() {
    let input = this.$el?.querySelector('input') ?? {}
    input.autocomplete = 'off'
  },
  beforeUnmount() {
    if (this.$refs.smartSelect) {
      this.$refs.smartSelect.deactivate()
    }
  },
  methods: {
    /**
     * If the inital value is an array of non-objects (string/number)
     * then return array of simple values.
     */
    returnValue(value) {
      // if (Array.isArray(this.initialValue) && typeof this.initialValue[0] != 'object') {
      let preparedValue = value
      if (Array.isArray(value)) {
        let val = value.map((item) => {
          return item.value
        })
        preparedValue = val
      } else if (typeof value == 'object' && false) {
        preparedValue = value
      } else {
        let val = value ? value[this._trackBy] : ''
        preparedValue = val ? val.toString() : value
      }

      // emit the whole object in case someone wants it
      this.$emit('object', value)

      // emit updateModel value
      this.$emit('update:modelValue', preparedValue)
    },

    /**
     * This is a hack until vue-multiselect v3.0 is out of beta.
     * See https://github.com/shentao/vue-multiselect/pull/654
     */
    handleTab(event) {
      if (this.selectOnTab && this.$refs.smartSelectAnt) {
        // only available focus and blur methods: no access to option
        this.$refs.smartSelectAnt.blur()
      }
    }
  }
}
</script>
<style>
body .v--modal-overlay .v--modal-box,
.v--modal-overlay .v--modal-box {
  overflow: visible;
}
.ant-select-selection-item .ant-select-selection-item-remove {
  display: flex !important;
}
/* .ant-tag {
  display: flex !important;
  align-items: center !important;
  background: rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(5, 5, 5, 0.06) !important;
  font-size: 14px !important;
} */
</style>
